import React, { useCallback, useEffect, useState } from "react";
import { getMaintenanceStatus, parseMaintenanceResult } from "../helper/Api";
import { applyPortalBranding } from "../helper/portalBranding";
import MaintenancePage from "../pages/Maintenance/MaintenancePage";
import Spinner from "../components/Tools/spinner";

const MaintenanceGate = ({ children }) => {
  const [loading, setLoading] = useState(true);
  const [retrying, setRetrying] = useState(false);
  /** null until BE returns a valid maintenance payload */
  const [maintenance, setMaintenance] = useState(null);
  const [fetchError, setFetchError] = useState("");

  const applyMaintenance = useCallback((result) => {
    const parsed = parseMaintenanceResult(result);
    applyPortalBranding({
      portalTitle: parsed.portalTitle,
      faviconUrl: parsed.faviconUrl,
      portalLogo: parsed.portalLogo,
    });
    setMaintenance(parsed);
    setFetchError("");
  }, []);

  const fetchStatus = useCallback(async (isRetry = false) => {
    if (isRetry) {
      setRetrying(true);
    } else {
      setLoading(true);
    }
    setFetchError("");
    try {
      const res = await getMaintenanceStatus();
      const body = res?.data;
      if (body?.status && body?.result && typeof body.result === "object") {
        applyMaintenance(body.result);
      } else {
        // No valid maintenance response — do not open the app
        setMaintenance(null);
        setFetchError(
          body?.message ||
            "Could not verify site status. Maintenance API did not return a valid response."
        );
      }
    } catch (error) {
      const body = error?.response?.data;
      const httpStatus = error?.response?.status;
      // Only treat as down when BE explicitly says so in a maintenance payload
      if (
        body &&
        (body.maintenance_mode === true ||
          body.site_up === false ||
          body.site_up === 0 ||
          body.site_up === "0")
      ) {
        applyMaintenance({
          site_up: false,
          title: body.title,
          message: body.message,
          portal_title: body.portal_title || body.title,
          favicon_url: body.favicon_url,
          portal_logo: body.portal_logo,
        });
      } else {
        setMaintenance(null);
        const detail =
          body?.message ||
          error?.message ||
          (httpStatus ? `HTTP ${httpStatus}` : "Network error");
        setFetchError(
          `Could not verify site status (${detail}). Fix the maintenance API, then retry.`
        );
      }
    } finally {
      setLoading(false);
      setRetrying(false);
    }
  }, [applyMaintenance]);

  useEffect(() => {
    fetchStatus(false);
  }, [fetchStatus]);

  useEffect(() => {
    const onMaintenance = (event) => {
      applyMaintenance(event?.detail || { site_up: false });
    };
    window.addEventListener("app-maintenance", onMaintenance);
    return () => window.removeEventListener("app-maintenance", onMaintenance);
  }, [applyMaintenance]);

  if (loading) {
    return (
      <div
        style={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: "#152456",
        }}
      >
        <Spinner />
      </div>
    );
  }

  // API failed / invalid — block app until we get a real maintenance response
  if (!maintenance) {
    return (
      <MaintenancePage
        title="Unable to verify site status"
        message={fetchError}
        onRetry={() => fetchStatus(true)}
        retrying={retrying}
      />
    );
  }

  if (!maintenance.siteUp) {
    return (
      <MaintenancePage
        title="Service Unavailable."
        message="Please contact your system administrator."
        onRetry={() => fetchStatus(true)}
        retrying={retrying}
      />
    );
  }

  return children;
};

export default MaintenanceGate;
